import { Transport, TransportRequest, TransportResponse } from "./Transport"
import { Sdk } from "../model/model"
import { Clock, SystemClock } from "../util/TimeUtil"
import {
  SDK_KEY_HEADER,
  SDK_KEY_PARAM,
  SDK_NAME_HEADER,
  SDK_NAME_PARAM,
  SDK_TIME_HEADER,
  SDK_TIME_PARAM,
  SDK_VERSION_HEADER,
  SDK_VERSION_PARAM
} from "../../../config"
import { TransportInterceptor } from "./TransportInterceptor"

export abstract class SdkInterceptor extends TransportInterceptor {
  protected constructor(protected readonly sdk: Sdk, protected readonly clock: Clock, transport: Transport) {
    super(transport)
  }

  static header(sdk: Sdk, transport: Transport, clock: Clock = SystemClock.instance): SdkInterceptor {
    return new SdkHeaderInterceptor(sdk, clock, transport)
  }

  static query(sdk: Sdk, transport: Transport, clock: Clock = SystemClock.instance): SdkInterceptor {
    return new SdkQueryInterceptor(sdk, clock, transport)
  }

  intercept(transport: Transport, request: TransportRequest): Promise<TransportResponse> {
    return transport.send(this.decorate(request))
  }

  protected abstract decorate(request: TransportRequest): TransportRequest
}

/**
 * for Node.js
 * - Adds SDK metadata as headers, same as the server SDKs.
 */
export class SdkHeaderInterceptor extends SdkInterceptor {
  constructor(sdk: Sdk, clock: Clock, transport: Transport) {
    super(sdk, clock, transport)
  }

  protected decorate(request: TransportRequest): TransportRequest {
    return request
      .toBuilder()
      .addHeader(SDK_KEY_HEADER, this.sdk.key)
      .addHeader(SDK_NAME_HEADER, this.sdk.name)
      .addHeader(SDK_VERSION_HEADER, this.sdk.version)
      .addHeader(SDK_TIME_HEADER, this.clock.currentMillis().toString())
      .build()
  }
}

/**
 * for Browser
 * - Adds SDK metadata as query params to avoid CORS preflight.
 */
export class SdkQueryInterceptor extends SdkInterceptor {
  constructor(sdk: Sdk, clock: Clock, transport: Transport) {
    super(sdk, clock, transport)
  }

  protected decorate(request: TransportRequest): TransportRequest {
    const url = request.url
      .toBuilder()
      .addParam(SDK_KEY_PARAM, this.sdk.key)
      .addParam(SDK_NAME_PARAM, this.sdk.name)
      .addParam(SDK_VERSION_PARAM, this.sdk.version)
      .addParam(SDK_TIME_PARAM, this.clock.currentMillis().toString())
      .build()
    return request.toBuilder().url(url).build()
  }
}
